import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_BASE_URL from './config';
import './Login.css';

const ResetPassword = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [validating, setValidating] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    // Validate token on component mount
    useEffect(() => {
        validateToken();
    }, [token]);

    const validateToken = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/auth/validate-reset-token/${token}`);
            const data = await response.json();
            
            if (response.ok && data.valid) {
                setTokenValid(true);
            } else {
                setTokenValid(false);
                setError(data.detail || 'Invalid or expired reset link');
            }
        } catch (err) {
            setTokenValid(false);
            setError('Failed to validate reset link');
            console.error('Token validation error:', err);
        } finally {
            setValidating(false);
        }
    };

    const validatePassword = () => {
        if (password.length < 8) {
            return 'Password must be at least 8 characters long';
        }
        if (password !== confirmPassword) {
            return 'Passwords do not match';
        }
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validate password
        const validationError = validatePassword();
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${API_BASE_URL}/auth/reset-password/${token}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ new_password: password }),
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess(true);
                // Redirect to login after 3 seconds
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else {
                setError(data.detail || 'Failed to reset password');
            }
        } catch (err) {
            setError('Network error. Please try again.');
            console.error('Password reset error:', err);
        } finally {
            setLoading(false);
        }
    };

    // Loading state while validating token
    if (validating) {
        return (
            <div className="login-container">
                <div className="login-box">
                    <h2>Password Reset</h2>
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
                        <p style={{ color: 'var(--text-secondary)', marginTop: '20px' }}>
                            Validating reset link...
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Invalid token
    if (!tokenValid) {
        return (
            <div className="login-container">
                <div className="login-box">
                    <h2>Invalid Reset Link</h2>
                    <div style={{ 
                        padding: '20px', 
                        background: '#fff5f5',
                        border: '1px solid #dc3545',
                        borderRadius: '8px',
                        marginTop: '20px',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
                        <p style={{ color: '#721c24', marginBottom: '10px' }}>
                            {error}
                        </p>
                        <p style={{ color: '#721c24', fontSize: '14px' }}>
                            Password reset links expire after 1 hour.
                        </p>
                    </div>
                    <div style={{ marginTop: '20px', textAlign: 'center' }}>
                        <a href="/reset-password" style={{ color: 'var(--primary)', textDecoration: 'none', marginRight: '20px' }}>
                            Request New Link
                        </a>
                        <a href="/login" style={{ color: 'var(--primary)', textDecoration: 'none' }}>
                            Back to Login
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    // Success state
    if (success) {
        return (
            <div className="login-container">
                <div className="login-box">
                    <h2>Password Reset Successful</h2>
                    <div style={{ 
                        padding: '20px', 
                        background: 'var(--bg-secondary)',
                        borderRadius: '8px',
                        marginTop: '20px',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
                        <p style={{ color: 'var(--text-primary)', marginBottom: '10px' }}>
                            Your password has been reset successfully!
                        </p>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                            Redirecting to login page...
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Reset password form
    return (
        <div className="login-container">
            <div className="login-box">
                <h2>Set New Password</h2>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
                    Please enter your new password below.
                </p>

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="password">New Password</label>
                        <input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            placeholder="At least 8 characters"
                            autoComplete="new-password"
                            disabled={loading}
                        />
                        <small style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                            Minimum 8 characters
                        </small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm Password</label>
                        <input
                            id="confirmPassword"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            placeholder="Re-enter your password"
                            autoComplete="new-password"
                            disabled={loading}
                        />
                    </div>

                    {error && (
                        <div style={{ 
                            color: '#dc3545', 
                            background: '#fff5f5',
                            padding: '10px',
                            borderRadius: '4px',
                            marginBottom: '15px',
                            fontSize: '14px'
                        }}>
                            {error}
                        </div>
                    )}

                    <button 
                        type="submit" 
                        className="login-btn"
                        disabled={loading}
                    >
                        {loading ? 'Resetting...' : 'Reset Password'}
                    </button>
                </form>

                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                    <a href="/login" style={{ color: 'var(--primary)', textDecoration: 'none' }}>
                        ← Back to Login
                    </a>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;

